import { Spin } from 'antd'
import { RegionOfInterestTable } from 'features/fde/regionOfInterest/RegionOfInterestTable'
import { useRolodexRulers } from 'features/fde/rulers/rulerApi'
import { getRulerLengthDetailsColumn } from 'features/fde/rulers/RulerComponents'
import { createRulerMarkup } from 'features/fde/rulers/rulerMarkup'
import { rulerToRegion } from 'features/fde/rulers/rulerTypes'
import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useIndicationDetailCardExtension } from 'features/indicationTriage/IndicationDetailCardExtensionContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useGeckoLocale } from 'features/locale'
import { useRolodexCache, useRolodexCachedEntries } from 'features/rolodex'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit, RolodexEntryJson } from 'types'

const Container = styled.div`
  height: 100%;
`

interface RulerCardConfig {
  custom_title?: string
}

export const Title = (
  _indication: IndicationItem,
  config?: Record<string, unknown>
) => (config?.custom_title as string | undefined) ?? 'Distances'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps<RulerCardConfig>> = () => {
  // Find ruler entries relevant to the indicationItem

  const {
    indication: indicationItem,
    component: selectedComponent,
    relevantThicknessCDF: cdf,
  } = useIndicationData()
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const zoomToRegion = useIndicationDetailCardExtension()?.zoomToRegion

  const { rulers, isLoading: rulersLoading } = useRolodexRulers(
    useMemo(
      () => (indicationItem ? { indication: indicationItem.id } : undefined),
      [indicationItem]
    )
  )

  const preferredDisplayUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.Feet
  )

  const rulerMarkups = useMemo(
    () =>
      cdf && indicationItem
        ? rulers
            .map((selectedRuler) =>
              createRulerMarkup(
                selectedRuler,
                cdf.data.geometry,
                selectedComponent ?? null,
                preferredDisplayUnit,
                { indication: indicationItem.id }
              )
            )
            .filter((markup) => !!markup)
        : ([] as RolodexEntryJson[]),
    [cdf, indicationItem, preferredDisplayUnit, rulers, selectedComponent]
  )

  const rdCache = useRolodexCache()
  useRolodexCachedEntries(rulerMarkups, rdCache)

  const regionsOfInterest = useMemo(() => {
    return rulers.map(rulerToRegion)
  }, [rulers])

  return (
    <Container>
      <Spin spinning={rulersLoading}>
        <RegionOfInterestTable
          loading={rulersLoading}
          hideTitle
          zoomToRegion={zoomToRegion}
          regions={regionsOfInterest}
          detailsColumn={
            cdf
              ? getRulerLengthDetailsColumn(
                  rulers,
                  cdf.data.geometry,
                  preferredDisplayUnit
                )
              : undefined
          }></RegionOfInterestTable>
      </Spin>
    </Container>
  )
}
